import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import ReservationFloorPlan from "./ReservationFloorPlan.js";
import ReservationCard from "../components/ReservationCard.js";
import { api } from "../App";
import ReservationTimeCenterScroll from "./ReservationTimeCenterScroll.js";
import { arrangeGroup } from "./tableArrangement.js";

const BoardWrap = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 350px) minmax(0, 1fr);
  gap: 16px;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    height: auto;
    overflow: visible;
  }
`;

const LeftPanel = styled.div`
  height: 100%;
  min-height: 0;
  background: #ffffff;
  border: none;
  min-width: 0;
  border-right: 1px solid #e4ebe9;
  padding-right: 12px;
  border-radius: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    border-right: none;
    padding-right: 0;
    height: auto;
    max-height: none;
    min-height: 0;
    order: 1;
  }
`;

const LeftControls = styled.div`
  padding: 8px;
  margin-bottom: 0;
  border-bottom: 1px solid #efe6dc;
  background: #ffffff;

  & > * {
    margin: 0 !important;
    width: 100%;
  }
`;

const RightPanel = styled.div`
  height: 100%;
  min-height: 0;
  min-width: 0;
  background: #ffffff;
  border: none;
  border-radius: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    display: none;
  }
`;

const FloorHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 0 0 8px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #e4ebe9;
  flex-shrink: 0;
`;

/* The time strip sits dead centre of the header. The zone picker and the action
   buttons both grow from zero, so they take equal shares of whatever is left
   and the strip lands in the middle regardless of how wide either of them is —
   which it did not when the picker was allowed to grow from a 160px basis and
   pushed the strip over towards the buttons. */
const FloorHeaderTimeCenter = styled.div`
  min-width: 0;
  /* Wide enough to show several times either side of the selected one, while
     still yielding to the picker and the buttons when the window is narrow. */
  flex: 0 1 720px;
  display: flex;
  justify-content: center;

  @media (max-width: 900px) {
    order: -1;
    flex: 1 1 100%;
    max-width: none;
  }
`;

/* No opacity switch here on purpose. The board keeps the previous floor on
   screen while the next one loads, so hiding it only produced a flash of
   nothing on every click. */
const FloorPlanArea = styled.div`
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ZonePickerBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 0;
  /* Grows from zero so it balances against the actions on the other side. */
  flex: 1 1 0;
  min-width: 0;
`;

const ZonePickerLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  color: #6d625d;
`;

const ZoneBubble = styled.button`
  border: 1px solid ${({ active }) => (active ? "#EE754C" : "#dce5e2")};
  background: ${({ active }) => (active ? "#fff1e8" : "#ffffff")};
  color: ${({ active }) => (active ? "#c7502b" : "#536a64")};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  max-width: 100%;
  overflow-wrap: anywhere;
  font-weight: 750;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: none;

  &:hover {
    border-color: #ee754c;
    background: #fff7f2;
  }
`;

const ZoneSelect = styled.select`
  min-width: 0;
  max-width: 100%;
  border: 1px solid #e2d8cf;
  border-radius: 9px;
  padding: 7px 10px;
  font-size: 0.88rem;
  color: #4b3335;
  background: #fff;
`;

const EmptyState = styled.div`
  padding: 22px;
  color: #7a6d68;
  text-align: center;
  border: 0;
  border-radius: 8px;
  background: #f3f6f5;
  font-size: 12px;
  font-weight: 650;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.76rem;
  color: #6d625d;
  margin-top: 0;
  padding: 0;
`;

const LegendDot = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 4px;
  margin-right: 5px;
  vertical-align: -2px;
  background: ${({ color }) => color};
  border: 1px ${({ $dashed }) => ($dashed ? "dashed #99b0ad" : "solid #cbbfb5")};
`;

const ReservationList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;

  scrollbar-width: thin;
  scrollbar-color: rgba(153, 176, 173, 0.38) transparent;

  &::-webkit-scrollbar {
    width: 3px;
    height: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(153, 176, 173, 0.38);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(153, 176, 173, 0.65);
  }

  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
`;

const FloorHeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
  /* Mirrors the zone picker, so the strip between them is centred. */
  flex: 1 1 0;
  min-width: 0;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;

const ArrangeNote = styled.button`
  width: 100%;
  margin-bottom: 6px;
  padding: 6px 10px;
  border: 1px solid #e3d8cf;
  border-radius: 9px;
  background: #fffaf4;
  color: #6d625d;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
`;

const FloorToolButton = styled.button`
  min-height: 32px;
  max-width: 100%;
  overflow-wrap: anywhere;
  border-radius: 7px;
  border: 1px solid ${({ active }) => (active ? "#EE754C" : "#ddd")};
  background: ${({ active }) => (active ? "#fff1e8" : "#fff")};
  color: ${({ active }) => (active ? "#EE754C" : "#4b3335")};
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;

  &:hover {
    border-color: #ee754c;
    background: #fff7f2;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

function normalizeDateForUrl(date) {
  if (!date) return "";

  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    return date.slice(0, 10);
  }

  const parsedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function normalizeTimeForUrl(time) {
  if (!time) return "";
  return String(time).slice(0, 5);
}

function timeToMinutes(time) {
  const cleanTime = normalizeTimeForUrl(time);
  const [hours, minutes] = cleanTime.split(":").map(Number);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;

  return hours * 60 + minutes;
}

function parseTableIds(value) {
  if (!value) return [];

  return String(value)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id));
}

function isCancelledReservation(reservation) {
  const status = String(reservation?.status || "").toLowerCase();

  return (
    status === "cancelled" ||
    status === "cancelled_customer" ||
    status === "cancelled_restaurant" ||
    status === "cancelled_by_customer" ||
    status === "cancelled_by_restaurant" ||
    status === "no_show" ||
    status === "completed"
  );
}

function getTableZoneId(table) {
  if (!table) return "unknown";

  if (table.place_of_table?.id) return String(table.place_of_table.id);
  if (table.place_of_table?.pk) return String(table.place_of_table.pk);

  if (typeof table.place_of_table === "number") {
    return String(table.place_of_table);
  }

  if (typeof table.place_of_table === "string") {
    return table.place_of_table;
  }

  if (table.place_of_table_id) return String(table.place_of_table_id);

  return "unknown";
}

function getTableZoneName(table) {
  if (!table) return "";

  if (table.place_of_table?.place_of_table) {
    return table.place_of_table.place_of_table;
  }

  if (table.place_of_table?.name) {
    return table.place_of_table.name;
  }

  if (typeof table.place_of_table === "string") {
    return table.place_of_table;
  }

  return "";
}

function getReservationZoneId(reservation) {
  const zone = reservation?.table_place_preference;

  if (!zone) return "";

  if (zone.id) return String(zone.id);
  if (zone.pk) return String(zone.pk);

  if (typeof zone === "number") return String(zone);

  return "";
}

function getReservationZoneName(reservation) {
  const zone = reservation?.table_place_preference;

  if (!zone) return "";

  if (zone.place_of_table) return zone.place_of_table;
  if (zone.name) return zone.name;

  if (typeof zone === "string") return zone;

  return "";
}

function getReservationPrimaryZoneId(reservation, allTables) {
  const tableIds = parseTableIds(reservation?.tablesused);

  const firstTable = (allTables || []).find((table) =>
    tableIds.includes(Number(table.id)),
  );

  if (firstTable) return getTableZoneId(firstTable);

  return getReservationZoneId(reservation) || null;
}

function reservationUsesZone(reservation, zoneId, allTables) {
  if (!reservation || !zoneId) return true;

  const tableIds = parseTableIds(reservation.tablesused);

  if (!tableIds.length) {
    return (
      String(getReservationPrimaryZoneId(reservation, allTables)) ===
      String(zoneId)
    );
  }

  return (allTables || []).some((table) => {
    return (
      tableIds.includes(Number(table.id)) &&
      String(getTableZoneId(table)) === String(zoneId)
    );
  });
}

function buildZoneOptions(allTables, reservations, t) {
  const zoneMap = new Map();

  (allTables || []).forEach((table) => {
    const zoneId = getTableZoneId(table);
    const tableZoneName = getTableZoneName(table);

    if (!zoneMap.has(zoneId)) {
      zoneMap.set(zoneId, {
        id: zoneId,
        label: tableZoneName || "",
      });
    } else if (tableZoneName && !zoneMap.get(zoneId).label) {
      zoneMap.set(zoneId, {
        ...zoneMap.get(zoneId),
        label: tableZoneName,
      });
    }
  });

  (reservations || []).forEach((reservation) => {
    const reservationZoneId = getReservationZoneId(reservation);
    const reservationZoneName = getReservationZoneName(reservation);

    if (reservationZoneId && reservationZoneName) {
      zoneMap.set(reservationZoneId, {
        id: reservationZoneId,
        label: reservationZoneName,
      });
    }
  });

  return Array.from(zoneMap.values()).map((zone, index) => ({
    id: zone.id,
    label: zone.label || t("ui.i18nFinalCleanup.roomNumber", { number: index + 1 }),
  }));
}

function parseDateTime(dateValue, timeValue) {
  const date = normalizeDateForUrl(dateValue);
  const time = normalizeTimeForUrl(timeValue);

  if (!date || !time) return null;

  const parsed = new Date(`${date}T${time}:00`);

  if (Number.isNaN(parsed.getTime())) return null;

  return parsed;
}

function getReservationEndDateTime(reservation) {
  const start = parseDateTime(reservation?.date, reservation?.time);

  if (!start) return null;

  if (reservation?.estimatedtime) {
    const end = parseDateTime(reservation.date, reservation.estimatedtime);

    if (end && end > start) return end;

    if (end && end <= start) {
      const nextDayEnd = new Date(end);
      nextDayEnd.setDate(nextDayEnd.getDate() + 1);
      return nextDayEnd;
    }
  }

  const fallback = new Date(start);
  fallback.setHours(fallback.getHours() + 2);
  return fallback;
}

function getReservationTimeRange(reservation) {
  const start = parseDateTime(reservation?.date, reservation?.time);
  const end = getReservationEndDateTime(reservation);

  if (!start || !end) {
    return {
      start: null,
      end: null,
    };
  }

  return {
    start,
    end,
  };
}

function reservationIsActiveAtDateTime(reservation, targetDateTime) {
  if (!reservation || !targetDateTime) return false;

  const range = getReservationTimeRange(reservation);

  if (!range.start || !range.end) return false;

  return range.start <= targetDateTime && range.end > targetDateTime;
}

function buildOccupiedByTableId(reservationsAtTime) {
  const map = new Map();

  (reservationsAtTime || []).forEach((reservation) => {
    if (isCancelledReservation(reservation)) return;

    const tableIds = parseTableIds(reservation.tablesused);

    tableIds.forEach((tableId) => {
      const key = String(tableId);
      const existing = map.get(key) || [];

      existing.push({
        id: reservation.id,
        full_name: reservation.full_name,
        time: reservation.time,
        estimatedtime: reservation.estimatedtime,
        number_of_people: reservation.number_of_people,
      });

      map.set(key, existing);
    });
  });

  return map;
}

function buildAvailabilityTablesForZone(zoneTables, occupiedByTableId) {
  return (zoneTables || [])
    .sort((a, b) => Number(a.table_number) - Number(b.table_number))
    .map((table) => {
      const occupiedReservations =
        occupiedByTableId.get(String(table.id)) || [];

      return {
        number: String(table.id),
        status: occupiedReservations.length > 0 ? "1" : "0",
        reservationIds: occupiedReservations.map((reservation) =>
          String(reservation.id),
        ),
        reservationDetails: occupiedReservations,
      };
    });
}

function buildSelectedTablesForReservation(reservation, zoneName) {
  const tableIds = parseTableIds(reservation?.tablesused);

  if (!reservation || !tableIds.length) return {};

  return {
    [zoneName]: tableIds.map((tableId) => ({
      number: String(tableId),
      status: "1",
      reservationIds: [String(reservation.id)],
      reservationDetails: [
        {
          id: reservation.id,
          full_name: reservation.full_name,
          time: reservation.time,
          estimatedtime: reservation.estimatedtime,
          number_of_people: reservation.number_of_people,
        },
      ],
    })),
  };
}

/**
 * The moments the floor actually changes.
 *
 * Listing every reservation start time produced entries that showed the same
 * plan twice — two bookings starting together, or one starting while nothing it
 * touches has turned over. What matters is turnover: the first step holds every
 * booking that opens the day, and a further step appears only where the set of
 * occupied tables differs from the step before, which is exactly when a table
 * has been given up and passed to the next booking.
 *
 * Candidates are every start and every end, since a table freeing up changes
 * the floor just as much as one being taken.
 */
function buildFloorTurnoverTimes(reservationsForRoom) {
  const active = (reservationsForRoom || []).filter(
    (reservation) => !isCancelledReservation(reservation),
  );

  const candidates = new Set();

  // Start times of bookings that actually hold tables. These are protected from
  // the de-duplication below: a start is the moment guests arrive, so the floor
  // has to be able to show it even when the set of held tables looks the same.
  const protectedStarts = new Set();

  active.forEach((reservation) => {
    const start = normalizeTimeForUrl(reservation.time);

    if (start) {
      candidates.add(start);

      if (parseTableIds(reservation.tablesused).length) {
        protectedStarts.add(start);
      }
    }

    const range = getReservationTimeRange(reservation);
    if (range.end) {
      // A sitting that runs past midnight ends outside the day; the turnover
      // still belongs at that clock time.
      candidates.add(
        `${String(range.end.getHours()).padStart(2, "0")}:${String(
          range.end.getMinutes(),
        ).padStart(2, "0")}`,
      );
    }
  });

  const sorted = Array.from(candidates).sort(
    (a, b) => timeToMinutes(a) - timeToMinutes(b),
  );

  // Which tables are held at a given clock time.
  const occupancyAt = (time) => {
    const held = new Set();
    active.forEach((reservation) => {
      const range = getReservationTimeRange(reservation);
      if (!range.start || !range.end) return;

      const at = parseDateTime(reservation.date, time);
      if (!at) return;

      if (range.start <= at && range.end > at) {
        parseTableIds(reservation.tablesused).forEach((id) => held.add(id));
      }
    });
    return Array.from(held).sort((a, b) => a - b).join(",");
  };

  const kept = [];
  let previous = null;

  for (const time of sorted) {
    const signature = occupancyAt(time);
    const isStart = protectedStarts.has(time);

    // Skip a moment that leaves the floor looking exactly as it did, and skip
    // the trailing empty floor after the last sitting ends - unless a booking
    // starts then, in which case the moment is the whole point.
    if (!isStart && signature === previous) continue;
    if (!isStart && !signature && kept.length) continue;

    kept.push(time);
    previous = signature;
  }

  return kept;
}

function getLayoutReservationId(layoutItem) {
  return String(
    layoutItem?.reservation_id ??
      layoutItem?.reservationId ??
      layoutItem?.reservation ??
      "",
  );
}

function getLayoutTableId(layoutItem) {
  return String(
    layoutItem?.table_id ?? layoutItem?.tableId ?? layoutItem?.table ?? "",
  );
}

function getLayoutKey(layoutItem) {
  return `${getLayoutReservationId(layoutItem)}:${getLayoutTableId(layoutItem)}`;
}

function mergeLayoutItems(existingItems = [], savedItems = []) {
  const map = new Map();

  existingItems.forEach((item) => {
    const key = getLayoutKey(item);
    if (key !== ":") map.set(key, item);
  });

  savedItems.forEach((item) => {
    const key = getLayoutKey(item);
    if (key !== ":") map.set(key, item);
  });

  return Array.from(map.values());
}

function mergeReservationsById(reservations = [], floorReservations = []) {
  const mergedMap = new Map();

  (reservations || []).forEach((reservation) => {
    if (!reservation?.id) return;
    mergedMap.set(String(reservation.id), reservation);
  });

  (floorReservations || []).forEach((reservation) => {
    if (!reservation?.id) return;

    const id = String(reservation.id);
    const existingReservation = mergedMap.get(id) || {};

    mergedMap.set(id, {
      ...existingReservation,
      ...reservation,
    });
  });

  return Array.from(mergedMap.values());
}

export default function DailyFloorBoard({
  reservations = [],
  floorReservations = [],
  allTables = [],
  restaurantId,
  restaurantTimezone = "Europe/London",
  dateFilter,
  workspaceHeight,
  leftControls = null,
  dispatch_reservation_wait = () => {},
  past_estimated_time = false,
  reservationLayoutSocketUpdate = null,
}) {
  const { t } = useTranslation();
  // The shared api instance carries no auth interceptor, so any endpoint behind
  // IsAuthenticated needs the token attached by hand. design-items is one.
  const { userInfo } = useSelector((state) => state.userLogin);
  const authConfig = useMemo(
    () => ({
      headers: userInfo?.token
        ? { Authorization: `Bearer ${userInfo.token}` }
        : {},
    }),
    [userInfo?.token],
  );
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  // The plan is drawn to the room's real dimensions and shows the elements the
  // room designer placed, so both are loaded alongside the tables.
  const [rooms, setRooms] = useState([]);
  const [objectsByRoom, setObjectsByRoom] = useState({});
  // Bumped after a whole-day organise so the floor reloads its overrides.
  const [floorRefreshToken, setFloorRefreshToken] = useState(0);
  // Every saved layout for the day, so a booking that has not started yet can
  // be drawn where it will actually sit.
  const [dayLayoutItems, setDayLayoutItems] = useState([]);
  const [activeZoneId, setActiveZoneId] = useState(null);
  const [selectedFloorTime, setSelectedFloorTime] = useState(null);

  const [reservationLayoutOverrides, setReservationLayoutOverrides] = useState(
    [],
  );
  const [reservationLayoutDraftItems, setReservationLayoutDraftItems] =
    useState([]);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [, setIsLoadingLayout] = useState(false);

  const [floorSnapshot, setFloorSnapshot] = useState(null);
  const [isFloorSwitching, setIsFloorSwitching] = useState(false);

  const floorLoadTimerRef = useRef(null);
  const floorLoadRequestIdRef = useRef(0);

  const visibleReservations = useMemo(() => {
    return (reservations || [])
      .filter((reservation) => !isCancelledReservation(reservation))
      .sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")));
  }, [reservations]);

  const visibleFloorReservations = useMemo(() => {
    return mergeReservationsById(reservations, floorReservations)
      .filter((reservation) => !isCancelledReservation(reservation))
      .sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")));
  }, [floorReservations, reservations]);

  const zoneOptions = useMemo(
    () => buildZoneOptions(allTables, visibleFloorReservations, t),
    [allTables, visibleFloorReservations, t],
  );

  const zoneOptionsKey = useMemo(
    () => zoneOptions.map((zone) => `${zone.id}:${zone.label}`).join("|"),
    [zoneOptions],
  );

  const effectiveActiveZoneId = activeZoneId || zoneOptions[0]?.id || null;

  const selectedReservation = useMemo(() => {
    const selectedFromFloorList = visibleFloorReservations.find(
      (reservation) => Number(reservation.id) === Number(selectedReservationId),
    );

    if (selectedFromFloorList) return selectedFromFloorList;

    const selectedFromVisibleList = visibleReservations.find(
      (reservation) => Number(reservation.id) === Number(selectedReservationId),
    );

    if (selectedFromVisibleList) return selectedFromVisibleList;

    return visibleFloorReservations[0] || visibleReservations[0] || null;
  }, [visibleReservations, visibleFloorReservations, selectedReservationId]);

  useEffect(() => {
    if (!zoneOptions.length) {
      setActiveZoneId(null);
      return;
    }

    setActiveZoneId((currentZoneId) => {
      const currentStillExists = zoneOptions.some(
        (zone) => String(zone.id) === String(currentZoneId),
      );

      if (currentZoneId && currentStillExists) return currentZoneId;

      const reservationZoneId = selectedReservation
        ? getReservationPrimaryZoneId(selectedReservation, allTables)
        : null;

      return reservationZoneId || zoneOptions[0]?.id || null;
    });
  }, [zoneOptionsKey, selectedReservation?.id, allTables, zoneOptions]);

  useEffect(() => {
    /* An empty list is almost always a refetch in flight, not a day with no
       bookings. Clearing the selection here dropped the chosen booking, and
       when the data came back the fallback re-seeded it with the first booking
       of the day - which is how a click could end up somewhere else entirely. */
    if (!visibleFloorReservations.length) return;

    if (!selectedReservation?.id) {
      setSelectedReservationId(null);
      return;
    }

    setSelectedReservationId((currentId) => {
      const currentStillExists = visibleFloorReservations.some(
        (reservation) => Number(reservation.id) === Number(currentId),
      );

      if (currentId && currentStillExists) return currentId;

      return selectedReservation.id;
    });
  }, [selectedReservation?.id, visibleFloorReservations]);

  const activeZone = zoneOptions.find(
    (zone) => String(zone.id) === String(effectiveActiveZoneId),
  );

  const activeZoneName = activeZone?.label || zoneOptions[0]?.label || "Planta";

  const activeZoneTables = useMemo(() => {
    if (!effectiveActiveZoneId) return [];

    return (allTables || []).filter(
      (table) =>
        String(getTableZoneId(table)) === String(effectiveActiveZoneId),
    );
  }, [allTables, effectiveActiveZoneId]);

  const roomReservations = useMemo(() => {
    return visibleFloorReservations.filter((reservation) =>
      reservationUsesZone(reservation, effectiveActiveZoneId, allTables),
    );
  }, [visibleFloorReservations, effectiveActiveZoneId, allTables]);

  const floorStartTimes = useMemo(() => {
    return buildFloorTurnoverTimes(visibleFloorReservations);
  }, [visibleFloorReservations]);

  const floorStartTimesKey = useMemo(
    () => floorStartTimes.join("|"),
    [floorStartTimes],
  );

  useEffect(() => {
    if (!floorStartTimes.length) {
      setSelectedFloorTime(null);
      return;
    }

    setSelectedFloorTime((currentTime) => {
      if (currentTime && floorStartTimes.includes(currentTime)) {
        return currentTime;
      }

      const selectedReservationTime = normalizeTimeForUrl(
        selectedReservation?.time,
      );

      if (selectedReservationTime) return selectedReservationTime;

      return floorStartTimes[0] || null;
    });
  }, [floorStartTimesKey, selectedReservation?.id, selectedReservation?.time]);

  const floorDate = normalizeDateForUrl(
    selectedReservation?.date ||
      visibleFloorReservations[0]?.date ||
      dateFilter,
  );

  const selectedFloorDateTime = useMemo(() => {
    if (!floorDate || !selectedFloorTime) return null;

    return parseDateTime(floorDate, selectedFloorTime);
  }, [floorDate, selectedFloorTime]);

  const activeFloorReservations = useMemo(() => {
    if (!selectedFloorDateTime) {
      return selectedReservation ? [selectedReservation] : [];
    }

    return visibleFloorReservations.filter((reservation) =>
      reservationIsActiveAtDateTime(reservation, selectedFloorDateTime),
    );
  }, [visibleFloorReservations, selectedFloorDateTime, selectedReservation]);

  const activeRoomFloorReservations = useMemo(() => {
    return activeFloorReservations.filter((reservation) =>
      reservationUsesZone(reservation, effectiveActiveZoneId, allTables),
    );
  }, [activeFloorReservations, effectiveActiveZoneId, allTables]);

  const layoutContextReservation = useMemo(() => {
    // Somebody picked this booking, so it is the context - full stop. Falling
    // back to another booking in the room was loading that one's layout and
    // taking the highlight with it.
    const isExplicitPick =
      selectedReservationId &&
      selectedReservation &&
      Number(selectedReservation.id) === Number(selectedReservationId);

    if (isExplicitPick) return selectedReservation;

    if (!activeRoomFloorReservations.length) return selectedReservation;

    const selectedStillActive = activeRoomFloorReservations.find(
      (reservation) =>
        Number(reservation.id) === Number(selectedReservation?.id),
    );

    return selectedStillActive || activeRoomFloorReservations[0];
  }, [activeRoomFloorReservations, selectedReservation, selectedReservationId]);

  const occupiedByTableId = useMemo(
    () => buildOccupiedByTableId(activeFloorReservations),
    [activeFloorReservations],
  );

  const availabilityTables = useMemo(
    () => buildAvailabilityTablesForZone(activeZoneTables, occupiedByTableId),
    [activeZoneTables, occupiedByTableId],
  );

  /**
   * Bookings that have not started yet, shown anyway.
   *
   * Looking at the 12:00 sitting, a 12:30 booking on a table nobody is using
   * right now tells the staff something useful: where that table has to end up.
   * Showing it lets them set the room out for a whole round in one pass instead
   * of stepping the clock forward and rearranging as they go.
   *
   * A table already taken at the selected moment is left alone — the booking
   * sitting there now is what matters, and the next one for that table would
   * only conflict with it.
   */
  const upcomingByTableId = useMemo(() => {
    if (!selectedFloorDateTime) return new Map();

    const takenNow = new Set(occupiedByTableId.keys());
    const result = new Map();

    visibleFloorReservations.forEach((reservation) => {
      if (isCancelledReservation(reservation)) return;

      const range = getReservationTimeRange(reservation);
      if (!range.start || range.start <= selectedFloorDateTime) return;

      parseTableIds(reservation.tablesused).forEach((tableId) => {
        const key = String(tableId);
        if (takenNow.has(key)) return;

        // Earliest booking wins: that is the one the table has to be ready for.
        const existing = result.get(key);
        if (existing && existing.start <= range.start) return;

        result.set(key, {
          start: range.start,
          reservation: {
            id: reservation.id,
            full_name: reservation.full_name,
            time: reservation.time,
            estimatedtime: reservation.estimatedtime,
            number_of_people: reservation.number_of_people,
          },
        });
      });
    });

    return result;
  }, [visibleFloorReservations, selectedFloorDateTime, occupiedByTableId]);

  const upcomingTables = useMemo(
    () =>
      activeZoneTables
        .filter((table) => upcomingByTableId.has(String(table.id)))
        .map((table) => {
          const entry = upcomingByTableId.get(String(table.id));
          return {
            number: String(table.id),
            status: "upcoming",
            reservationIds: [String(entry.reservation.id)],
            reservationDetails: [entry.reservation],
          };
        }),
    [activeZoneTables, upcomingByTableId],
  );

  /**
   * Where those not-yet-started bookings put their tables.
   *
   * Each booking keeps its own layout, so a table's future position comes from
   * that booking's override rather than from the one on screen now.
   */
  const upcomingLayoutOverrides = useMemo(() => {
    if (!upcomingByTableId.size || !dayLayoutItems.length) return [];

    const wanted = new Map();
    upcomingByTableId.forEach((entry, tableId) => {
      wanted.set(`${entry.reservation.id}:${tableId}`, true);
    });

    return dayLayoutItems.filter((item) =>
      wanted.has(
        `${item.reservation_id}:${item.table_id ?? item.tableId}`,
      ),
    );
  }, [upcomingByTableId, dayLayoutItems]);

  const selectedReservationIsActiveAtSelectedTime = useMemo(() => {
    if (!selectedReservation) return false;
    if (!selectedFloorDateTime) return true;

    return reservationIsActiveAtDateTime(
      selectedReservation,
      selectedFloorDateTime,
    );
  }, [selectedReservation, selectedFloorDateTime]);

  const selectedTables = useMemo(() => {
    if (!selectedReservationIsActiveAtSelectedTime) return {};

    return buildSelectedTablesForReservation(
      selectedReservation,
      activeZoneName,
    );
  }, [
    selectedReservation,
    activeZoneName,
    selectedReservationIsActiveAtSelectedTime,
  ]);

  const floorContextKey = useMemo(() => {
    return [
      restaurantId || "",
      layoutContextReservation?.id || "no-reservation",
      effectiveActiveZoneId || "no-zone",
      selectedFloorTime || "no-time",
      activeZoneName || "no-zone-name",
      floorRefreshToken,
    ].join("|");
  }, [
    restaurantId,
    layoutContextReservation?.id,
    effectiveActiveZoneId,
    selectedFloorTime,
    activeZoneName,
    floorRefreshToken,
  ]);

  /**
   * The selected booking's saved positions, straight from the day's cache.
   *
   * Every layout for the day already arrives in one request, so asking the
   * server again for one booking's rows is a round trip for data in hand. This
   * is what makes clicking a time or a booking feel immediate instead of
   * fading the plan out and waiting.
   */
  const cachedLayoutOverrides = useMemo(() => {
    const reservationId = layoutContextReservation?.id;

    if (!reservationId || !dayLayoutItems.length) return null;

    const items = dayLayoutItems.filter(
      (item) => Number(item.reservation_id) === Number(reservationId),
    );

    return items.length ? items : null;
  }, [dayLayoutItems, layoutContextReservation?.id]);

  const selectedReservationRef = useRef(null);
  selectedReservationRef.current = selectedReservation;

  const handleReservationSelect = useCallback((reservation, options = {}) => {
    if (!reservation?.id) return;

    const { keepCurrentFloorTime = false, keepCurrentZone = false } = options;

    setSelectedReservationId(reservation.id);

    if (!keepCurrentFloorTime) {
      setSelectedFloorTime(normalizeTimeForUrl(reservation.time));
    }

    if (!keepCurrentZone) {
      const zoneId = getReservationPrimaryZoneId(reservation, allTables);

      if (zoneId) setActiveZoneId(zoneId);
    }
  }, [allTables]);

  const handleFloorTimeSelect = useCallback((time) => {
    const cleanTime = normalizeTimeForUrl(time);

    setSelectedFloorTime(cleanTime);

    const targetDateTime = floorDate
      ? parseDateTime(floorDate, cleanTime)
      : null;

    /* Already looking at a booking that belongs to this moment? Leave it alone.
       Re-picking the room's first booking at that time is the other half of why
       clicking one booking landed on another: anything that re-issued the
       current time reset the selection to whoever happened to be first. */
    const alreadySelected = selectedReservationRef.current;

    if (alreadySelected) {
      if (normalizeTimeForUrl(alreadySelected.time) === cleanTime) return;

      if (
        targetDateTime &&
        reservationIsActiveAtDateTime(alreadySelected, targetDateTime)
      ) {
        return;
      }
    }

    const startingReservationInCurrentRoom =
      roomReservations.find(
        (reservation) => normalizeTimeForUrl(reservation.time) === cleanTime,
      ) || null;

    if (startingReservationInCurrentRoom) {
      handleReservationSelect(startingReservationInCurrentRoom, {
        keepCurrentFloorTime: true,
      });

      return;
    }

    const activeReservationInCurrentRoom = targetDateTime
      ? roomReservations.find((reservation) =>
          reservationIsActiveAtDateTime(reservation, targetDateTime),
        )
      : null;

    if (activeReservationInCurrentRoom) {
      handleReservationSelect(activeReservationInCurrentRoom, {
        keepCurrentFloorTime: true,
        keepCurrentZone: true,
      });

      return;
    }

    const startingReservationInAnyRoom =
      visibleFloorReservations.find(
        (reservation) => normalizeTimeForUrl(reservation.time) === cleanTime,
      ) ||
      visibleReservations.find(
        (reservation) => normalizeTimeForUrl(reservation.time) === cleanTime,
      ) ||
      null;

    if (startingReservationInAnyRoom) {
      handleReservationSelect(startingReservationInAnyRoom, {
        keepCurrentFloorTime: true,
      });

      return;
    }

    const activeReservationInAnyRoom = targetDateTime
      ? visibleFloorReservations.find((reservation) =>
          reservationIsActiveAtDateTime(reservation, targetDateTime),
        )
      : null;

    if (!activeReservationInAnyRoom) return;

    handleReservationSelect(activeReservationInAnyRoom, {
      keepCurrentFloorTime: true,
    });
  }, [
    floorDate,
    handleReservationSelect,
    roomReservations,
    visibleFloorReservations,
    visibleReservations,
  ]);

  const handleFloorTableSelect = useCallback((tableInfo) => {
    const reservationDetails = tableInfo?.reservationDetails || [];

    if (!reservationDetails.length) return;

    const nextReservation =
      reservationDetails.find(
        (reservation) =>
          Number(reservation.id) !== Number(selectedReservation?.id),
      ) || reservationDetails[0];

    if (!nextReservation?.id) return;

    const fullReservation =
      visibleFloorReservations.find(
        (reservation) => Number(reservation.id) === Number(nextReservation.id),
      ) ||
      visibleReservations.find(
        (reservation) => Number(reservation.id) === Number(nextReservation.id),
      );

    if (!fullReservation) return;

    // Clicking an occupied table should select that reservation
    // AND jump the floor time to that reservation's start time.
    handleReservationSelect(fullReservation);
  }, [
    handleReservationSelect,
    selectedReservation?.id,
    visibleFloorReservations,
    visibleReservations,
  ]);

  useEffect(() => {
    if (!reservationLayoutSocketUpdate?.reservationId) return;
    if (!selectedReservation?.id) return;

    const updatedReservationId = String(
      reservationLayoutSocketUpdate.reservationId,
    );

    const incomingItems = Array.isArray(
      reservationLayoutSocketUpdate.layoutItems,
    )
      ? reservationLayoutSocketUpdate.layoutItems
      : [];

    setFloorSnapshot((previousSnapshot) => {
      if (!previousSnapshot) return previousSnapshot;

      const previousWithoutUpdatedReservation =
        previousSnapshot.layoutOverrides.filter(
          (item) => getLayoutReservationId(item) !== updatedReservationId,
        );

      const normalizedIncomingItems = incomingItems.map((item) => ({
        ...item,
        reservation_id:
          item.reservation_id ?? item.reservationId ?? updatedReservationId,
      }));

      return {
        ...previousSnapshot,
        layoutOverrides: [
          ...previousWithoutUpdatedReservation,
          ...normalizedIncomingItems,
        ],
      };
    });
  }, [reservationLayoutSocketUpdate, selectedReservation?.id]);

  const floorViewInputsRef = useRef(null);
  floorViewInputsRef.current = {
    activeZoneName,
    availabilityTables,
    selectedTables,
  };

  // Occupancy moves under a settled floor - a booking is seated, a status
  // changes - so refresh what the plan draws without re-running the loader.
  useEffect(() => {
    setFloorSnapshot((previousSnapshot) => {
      if (!previousSnapshot) return previousSnapshot;

      // The loader usually wrote these a moment ago. Returning the same object
      // lets React skip the render instead of committing an identical tree.
      if (
        previousSnapshot.activeZoneName === activeZoneName &&
        previousSnapshot.availabilityTables === availabilityTables &&
        previousSnapshot.selectedTables === selectedTables
      ) {
        return previousSnapshot;
      }

      return {
        ...previousSnapshot,
        activeZoneName,
        availabilityTables,
        selectedTables,
      };
    });
  }, [activeZoneName, availabilityTables, selectedTables]);

  useEffect(() => {
    const requestId = floorLoadRequestIdRef.current + 1;
    floorLoadRequestIdRef.current = requestId;

    if (floorLoadTimerRef.current) {
      clearTimeout(floorLoadTimerRef.current);
    }

    setReservationLayoutDraftItems([]);

    const makeSnapshot = (layoutOverridesForSnapshot = []) => ({
      key: floorContextKey,
      ...floorViewInputsRef.current,
      layoutOverrides: layoutOverridesForSnapshot,
    });

    // Cache hit: no debounce, no request, nothing to wait for.
    if (cachedLayoutOverrides) {
      setReservationLayoutOverrides(cachedLayoutOverrides);
      setFloorSnapshot(makeSnapshot(cachedLayoutOverrides));
      setIsFloorSwitching(false);

      return undefined;
    }

    setIsFloorSwitching(true);

    floorLoadTimerRef.current = setTimeout(async () => {
      if (!restaurantId || !layoutContextReservation?.id) {
        if (requestId !== floorLoadRequestIdRef.current) return;

        setReservationLayoutOverrides([]);
        setFloorSnapshot(makeSnapshot([]));

        requestAnimationFrame(() => {
          if (requestId === floorLoadRequestIdRef.current) {
            setIsFloorSwitching(false);
          }
        });

        return;
      }

      try {
        setIsLoadingLayout(true);

        const response = await api.get(
          `restaurants/${restaurantId}/reservation-table-layout/${layoutContextReservation.id}/`,
        );

        if (requestId !== floorLoadRequestIdRef.current) return;

        const responseData = Array.isArray(response.data) ? response.data : [];

        setReservationLayoutOverrides(responseData);
        setFloorSnapshot(makeSnapshot(responseData));
      } catch (error) {
        if (requestId !== floorLoadRequestIdRef.current) return;

        console.error("Failed to load reservation floor layout:", error);

        setReservationLayoutOverrides([]);
        setFloorSnapshot(makeSnapshot([]));
      } finally {
        if (requestId === floorLoadRequestIdRef.current) {
          setIsLoadingLayout(false);

          requestAnimationFrame(() => {
            if (requestId === floorLoadRequestIdRef.current) {
              setIsFloorSwitching(false);
            }
          });
        }
      }
    }, 120);

    return () => {
      if (floorLoadTimerRef.current) {
        clearTimeout(floorLoadTimerRef.current);
      }
    };
  }, [
    floorContextKey,
    restaurantId,
    layoutContextReservation?.id,
    cachedLayoutOverrides,
    setIsLoadingLayout,
  ]);

  useEffect(() => {
    if (!restaurantId) {
      setRooms([]);
      return undefined;
    }

    let cancelled = false;

    api
      .get(`restaurants/${restaurantId}/placeoftable/`, authConfig)
      .then((response) => {
        if (cancelled) return;
        setRooms(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load rooms for the floor plan:", error);
        setRooms([]);
      });

    return () => {
      cancelled = true;
    };
  }, [restaurantId, authConfig]);

  // Elements are decoration: the design-items endpoint requires layout-edit
  // rights, so a staff member without them still gets the plan, just without
  // the kitchen and walls drawn behind the tables.
  //
  // Every room is loaded, not only the active one, because organising the whole
  // day has to respect the walls and doorways of each room it touches.
  useEffect(() => {
    if (!restaurantId || !rooms.length) {
      setObjectsByRoom({});
      return undefined;
    }

    let cancelled = false;

    Promise.all(
      rooms.map((room) =>
        api
          .get(
            `restaurants/${restaurantId}/design-items/?place_of_table=${room.id}`,
            authConfig,
          )
          .then((response) => [
            String(room.id),
            Array.isArray(response.data) ? response.data : [],
          ])
          .catch((error) => {
            console.warn(
              `Room elements not loaded for room ${room.id}:`,
              error?.response?.status || error?.message,
            );
            return [String(room.id), []];
          }),
      ),
    ).then((entries) => {
      if (cancelled) return;
      setObjectsByRoom(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [restaurantId, rooms, authConfig]);

  const roomObjects = useMemo(
    () => objectsByRoom[String(effectiveActiveZoneId)] || [],
    [objectsByRoom, effectiveActiveZoneId],
  );

  const activeRoom = useMemo(() => {
    if (!effectiveActiveZoneId) return null;

    return (
      (rooms || []).find(
        (room) => String(room.id) === String(effectiveActiveZoneId),
      ) || null
    );
  }, [rooms, effectiveActiveZoneId]);

  // Push the selected booking's tables together into one block, obeying the
  // service-aisle, corridor and pinned-table rules. Results land in the same
  // draft the manual drag fills, so "Guardar" and "Descartar" work unchanged.
  const [arrangeMessage, setArrangeMessage] = useState("");

  useEffect(() => {
    setArrangeMessage("");
  }, [layoutContextReservation?.id, effectiveActiveZoneId, selectedFloorTime]);


  const [isOrganising, setIsOrganising] = useState(false);

  /**
   * Organise the entire day, every room, in one action.
   *
   * Each booking that spans more than one table is arranged independently and
   * against the room's own base layout, because layouts are stored per
   * reservation: the 13:00 sitting and the 20:00 sitting on the same tables get
   * their own arrangement and neither disturbs the other. Tables held by a
   * different booking that overlaps in time are obstacles, so two parties
   * sitting at once never get pushed into each other.
   */
  const computeArrangements = useCallback(
    (shouldArrange) => {
      const perReservation = new Map();
      let examined = 0;
      let refusedPins = 0;
      let refusedRoom = 0;

      for (const room of rooms) {
        const roomWidthCm = Number(room.width) * 100;
        const roomHeightCm = Number(room.height) * 100;
        if (!(roomWidthCm > 0) || !(roomHeightCm > 0)) continue;

        const roomCm = { width: roomWidthCm, height: roomHeightCm };
        const elements = objectsByRoom[String(room.id)] || [];
        const roomTables = (allTables || []).filter(
          (table) => String(getTableZoneId(table)) === String(room.id),
        );
        if (!roomTables.length) continue;

        const toEngine = (table) => ({
          id: table.id,
          left: Number(table.left_cm) || 0,
          top: Number(table.top_cm) || 0,
          width: Number(table.real_width_cm) || 80,
          height: Number(table.real_height_cm) || 80,
          rotation: Number(table.rotation) || 0,
          isLocked: table.is_locked === true || table.is_locked === 1,
        });

        const roomBookings = visibleFloorReservations
          .filter(
            (reservation) =>
              !isCancelledReservation(reservation) &&
              parseTableIds(reservation.tablesused).some((id) =>
                roomTables.some((table) => Number(table.id) === Number(id)),
              ),
          )
          // Earliest first. A booking has to avoid where its neighbours end up,
          // not where they started, so the ones already arranged are the
          // authority for the ones that follow.
          .sort((a, b) => {
            const left = getReservationTimeRange(a).start;
            const right = getReservationTimeRange(b).start;
            if (!left || !right) return 0;
            return left - right;
          });

        // "<reservation id>:<table id>" -> the position that booking gave it.
        const settled = new Map();

        for (const reservation of roomBookings) {
          const groupIds = parseTableIds(reservation.tablesused).map(String);
          if (groupIds.length < 2) continue;
          if (shouldArrange && !shouldArrange(reservation)) continue;
          examined += 1;

          const range = getReservationTimeRange(reservation);

          // Everything else sitting while this booking is seated, at the
          // position its own arrangement settled on where one exists. Using the
          // base layout here let two parties be arranged into each other: each
          // avoided where the other used to be.
          const occupiedNow = new Map();
          roomBookings.forEach((other) => {
            if (Number(other.id) === Number(reservation.id)) return;
            const otherRange = getReservationTimeRange(other);
            if (!range.start || !otherRange.start) return;
            const overlaps =
              range.start < otherRange.end && otherRange.start < range.end;
            if (!overlaps) return;
            parseTableIds(other.tablesused).forEach((id) => {
              occupiedNow.set(id, settled.get(`${other.id}:${id}`) || null);
            });
          });

          const groupTables = roomTables
            .filter((table) => groupIds.includes(String(table.id)))
            .map(toEngine);
          const otherTables = roomTables
            .filter(
              (table) =>
                !groupIds.includes(String(table.id)) &&
                occupiedNow.has(table.id),
            )
            .map((table) => {
              const placed = occupiedNow.get(table.id);
              const base = toEngine(table);
              return placed ? { ...base, left: placed.left, top: placed.top } : base;
            });

          const result = arrangeGroup({
            groupTables,
            otherTables,
            elements,
            room: roomCm,
          });

          if (!result.moves.length) {
            if (result.reason === "pinned-tables-apart") refusedPins += 1;
            else if (result.reason === "no-valid-arrangement") refusedRoom += 1;
            continue;
          }

          const legacyScale = Math.min(
            750 / roomWidthCm,
            400 / roomHeightCm,
          );

          const items = result.moves.map((move) => {
            const table = roomTables.find(
              (candidate) => Number(candidate.id) === Number(move.id),
            );
            const widthCm = Number(table?.real_width_cm) || 80;
            const heightCm = Number(table?.real_height_cm) || 80;

            return {
              table_id: move.id,
              left: Math.round(move.left * legacyScale),
              top: Math.round(move.top * legacyScale),
              width: Math.max(1, Math.round(widthCm * legacyScale)),
              height: Math.max(1, Math.round(heightCm * legacyScale)),
              left_cm: move.left,
              top_cm: move.top,
              width_cm: widthCm,
              height_cm: heightCm,
              isPlaced: true,
              rotation: Number(table?.rotation) || 0,
            };
          });

          items.forEach((item) => {
            settled.set(`${reservation.id}:${item.table_id}`, {
              left: item.left_cm,
              top: item.top_cm,
            });
          });

          perReservation.set(reservation.id, items);
        }
      }

      return { perReservation, examined, refusedPins, refusedRoom };
    },
    [
      restaurantId,
      rooms,
      objectsByRoom,
      allTables,
      visibleFloorReservations,
    ],
  );

  const saveArrangements = useCallback(
    async (perReservation) => {
      let saved = 0;
      let movedTables = 0;

      // One request per reservation: the endpoint attributes a moved table to
      // the booking that holds it at the selected reservation's time, so a
      // single call cannot cover bookings from across the whole day.
      for (const [reservationId, items] of perReservation) {
        try {
          await api.patch(
            `restaurants/${restaurantId}/reservation-table-layout/${reservationId}/`,
            { items },
            authConfig,
          );
          saved += 1;
          movedTables += items.length;
        } catch (error) {
          console.error(
            `Could not save the arrangement for reservation ${reservationId}:`,
            error?.response?.data || error?.message,
          );
        }
      }

      return { saved, movedTables };
    },
    [restaurantId, authConfig],
  );

  const organiseWholeDay = async () => {
    if (!restaurantId) return;

    setIsOrganising(true);
    setArrangeMessage("");

    try {
      const { perReservation, examined, refusedPins, refusedRoom } =
        computeArrangements();

      if (!perReservation.size) {
        setArrangeMessage(
          examined
            ? t("floorArrange.nothingToDo", {
                pins: refusedPins,
                tight: refusedRoom,
              })
            : t("floorArrange.noGroupsToday"),
        );
        return;
      }

      const { saved, movedTables } = await saveArrangements(perReservation);

      setArrangeMessage(
        t("floorArrange.dayDone", {
          bookings: saved,
          tables: movedTables,
          skipped: examined - perReservation.size,
        }),
      );

      // Pull the freshly saved layout back for the reservation on screen.
      setFloorRefreshToken((value) => value + 1);
    } catch (error) {
      setArrangeMessage(
        error?.response?.data?.detail || t("floorArrange.failed"),
      );
    } finally {
      setIsOrganising(false);
    }
  };

  /**
   * Arrange a new group booking on its own, as soon as the board sees it.
   *
   * "Immediately" is scoped to the floor board being open. The arrangement
   * rules live in one place — the JavaScript engine this screen already uses —
   * and duplicating them in Python so the server could run them on booking
   * creation would mean two implementations of the same geometry that have to
   * stay in step. A booking taken while nobody is watching is arranged the
   * moment somebody opens the board, which during a service is the same thing.
   *
   * Bookings whose layout is already on file are left alone, so this never
   * overwrites a position somebody set by hand.
   */
  const [layoutsOnFile, setLayoutsOnFile] = useState(null);
  const autoArrangedRef = useRef(new Set());

  useEffect(() => {
    if (!restaurantId || !floorDate) {
      setLayoutsOnFile(null);
      return undefined;
    }

    let cancelled = false;

    api
      .get(
        `restaurants/${restaurantId}/reservation-layouts-for-day/${floorDate}/`,
        authConfig,
      )
      .then((response) => {
        if (cancelled) return;
        const ids = Array.isArray(response.data?.reservation_ids)
          ? response.data.reservation_ids
          : [];
        setLayoutsOnFile(new Set(ids.map(String)));
        setDayLayoutItems(
          Array.isArray(response.data?.layout_items)
            ? response.data.layout_items
            : [],
        );
      })
      .catch((error) => {
        if (cancelled) return;
        // Without this list the automatic pass cannot tell a new booking from
        // one somebody has already arranged, so it stays off rather than risk
        // overwriting manual work.
        console.warn(
          "Could not load which bookings already have a layout:",
          error?.response?.status || error?.message,
        );
        setLayoutsOnFile(null);
        setDayLayoutItems([]);
      });

    return () => {
      cancelled = true;
    };
  }, [restaurantId, floorDate, floorRefreshToken, authConfig]);

  useEffect(() => {
    if (!layoutsOnFile || !rooms.length || isOrganising) return undefined;
    // Elements change the result, so wait until they have arrived.
    if (!Object.keys(objectsByRoom).length) return undefined;

    const isNew = (reservation) => {
      const id = String(reservation.id);
      return !layoutsOnFile.has(id) && !autoArrangedRef.current.has(id);
    };

    const candidates = visibleFloorReservations.filter(
      (reservation) =>
        !isCancelledReservation(reservation) &&
        parseTableIds(reservation.tablesused).length > 1 &&
        isNew(reservation),
    );

    if (!candidates.length) return undefined;

    const { perReservation } = computeArrangements(isNew);

    // Mark every candidate as handled, including ones the rules could not
    // satisfy, so this does not retry on each render.
    candidates.forEach((reservation) =>
      autoArrangedRef.current.add(String(reservation.id)),
    );

    if (!perReservation.size) return undefined;

    let cancelled = false;

    (async () => {
      const { saved, movedTables } = await saveArrangements(perReservation);
      if (cancelled || !saved) return;

      setArrangeMessage(
        t("floorArrange.autoDone", { bookings: saved, tables: movedTables }),
      );
      setFloorRefreshToken((value) => value + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    layoutsOnFile,
    rooms,
    objectsByRoom,
    visibleFloorReservations,
    computeArrangements,
    saveArrangements,
    isOrganising,
    t,
  ]);

  const handleReservationLayoutItemChange = useCallback((layoutItem) => {
    setReservationLayoutDraftItems((prevItems) => {
      const nextItems = [...prevItems];

      const existingIndex = nextItems.findIndex(
        (item) => String(item.table_id) === String(layoutItem.table_id),
      );

      if (existingIndex >= 0) {
        nextItems[existingIndex] = {
          ...nextItems[existingIndex],
          ...layoutItem,
        };
      } else {
        nextItems.push(layoutItem);
      }

      return nextItems;
    });
  }, []);

  const discardReservationLayoutDraft = () => {
    setReservationLayoutDraftItems([]);
  };

  const saveReservationLayoutDraft = async () => {
    if (!reservationLayoutDraftItems.length) return;

    if (!restaurantId || !layoutContextReservation?.id) {
      console.error(
        "Missing restaurant/reservation for saving daily floor layout.",
      );
      return;
    }

    try {
      setIsSavingLayout(true);

      const cleanLayoutItems = reservationLayoutDraftItems
        .filter((item) => item && item.table_id)
        .map((item) => ({
          table_id: item.table_id,
          left: Number(item.left || 0),
          top: Number(item.top || 0),
          width: Number(item.width || 50),
          height: Number(item.height || 50),
          // The centimetre pair has to survive this hop too. Rebuilding the
          // payload without it silently dropped back to 5 cm pixel precision,
          // which is what broke the joins between pushed-together tables.
          left_cm: item.left_cm,
          top_cm: item.top_cm,
          width_cm: item.width_cm,
          height_cm: item.height_cm,
          isPlaced: item.isPlaced !== false,
          rotation: Number(item.rotation || 0),
        }));

      const response = await api.patch(
        `restaurants/${restaurantId}/reservation-table-layout/${layoutContextReservation.id}/`,
        {
          items: cleanLayoutItems,
        },
      );

      const savedItems = Array.isArray(response.data?.layout_items)
        ? response.data.layout_items
        : Array.isArray(response.data?.items)
          ? response.data.items
          : [];

      setReservationLayoutOverrides((prevItems) =>
        mergeLayoutItems(prevItems, savedItems),
      );

      // The day cache now serves every selection, so a save has to land there
      // too or re-selecting this booking would show the old positions.
      setDayLayoutItems((prevItems) => mergeLayoutItems(prevItems, savedItems));

      setFloorSnapshot((previousSnapshot) => {
        if (!previousSnapshot) return previousSnapshot;

        return {
          ...previousSnapshot,
          layoutOverrides: mergeLayoutItems(
            previousSnapshot.layoutOverrides,
            savedItems,
          ),
        };
      });

      setReservationLayoutDraftItems([]);
      } catch (error) {
      console.error(
        "Failed to save daily floor layout:",
        error.response?.data || error,
      );
    } finally {
      setIsSavingLayout(false);
    }
  };

  /* The legend rides in the plan's footer bar, beside the zoom control, so
     nothing sits below the zoom. It never changes, so it is built once. */
  const legend = useMemo(
    () => (
      <Legend>
        <span>
          <LegendDot color="#F8F6F2" />
          {t("ui.components.changeTables.livre")}
        </span>

        <span>
          <LegendDot color="#99B0AD" />
          {t("ui.components.changeTables.ocupada")}
        </span>

        <span>
          <LegendDot color="#EE561B" />
          {t("ui.components.changeTables.selecionada")}
        </span>

        <span>
          <LegendDot color="#dfe9e6" $dashed />
          {t("floorArrange.legendUpcoming")}
        </span>
      </Legend>
    ),
    [t],
  );

  const displayedFloor = floorSnapshot || {
    activeZoneName,
    availabilityTables,
    selectedTables,
    layoutOverrides: reservationLayoutOverrides,
  };

  return (
    <BoardWrap $workspaceHeight={workspaceHeight}>
      <LeftPanel>
        {leftControls && <LeftControls>{leftControls}</LeftControls>}

        <ReservationList>
          {visibleReservations.length > 0 ? (
            <ReservationCard
              reservations={visibleReservations}
              reservations_complete={reservations}
              isReservationFromRestaurantScreen={true}
              dispatch_reservation_wait={dispatch_reservation_wait}
              past_estimated_time={past_estimated_time}
              variant="floorBoard"
              selectedReservationId={selectedReservation?.id}
              onReservationSelect={handleReservationSelect}
            />
          ) : (
            <EmptyState>{t("ui.i18nCleanup.noReservationsForFilters")}</EmptyState>
          )}
        </ReservationList>
      </LeftPanel>

      <RightPanel>
        <FloorHeader>
        {zoneOptions.length > 0 && (
          <ZonePickerBox>
            <ZonePickerLabel>{t("ui.components.tablesWithoutDragging.sala")}</ZonePickerLabel>

            {zoneOptions.length <= 4 ? (
              zoneOptions.map((zone) => (
                <ZoneBubble
                  key={zone.id}
                  type="button"
                  active={String(zone.id) === String(effectiveActiveZoneId)}
                  onClick={() => setActiveZoneId(zone.id)}
                >
                  {zone.label}
                </ZoneBubble>
              ))
            ) : (
              <ZoneSelect
                value={effectiveActiveZoneId || ""}
                onChange={(event) => setActiveZoneId(event.target.value)}
              >
                {zoneOptions.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.label}
                  </option>
                ))}
              </ZoneSelect>
            )}
          </ZonePickerBox>
        )}

          {floorStartTimes.length > 0 && <FloorHeaderTimeCenter>
            <ReservationTimeCenterScroll
              times={floorStartTimes}
              value={selectedFloorTime}
              onChange={handleFloorTimeSelect}
              restaurantTimezone={restaurantTimezone}
            />
          </FloorHeaderTimeCenter>}

          <FloorHeaderActions>
            <FloorToolButton
              type="button"
              onClick={organiseWholeDay}
              disabled={isSavingLayout || isOrganising || !rooms.length}
              title={t("floorArrange.hint")}
            >
              {isOrganising
                ? t("floorArrange.working")
                : t("floorArrange.action")}
            </FloorToolButton>

            {reservationLayoutDraftItems.length > 0 && (
              <>
                <FloorToolButton
                  type="button"
                  onClick={discardReservationLayoutDraft}
                  disabled={isSavingLayout}
                >
                  {t("ui.i18nCleanup.discard")}
                </FloorToolButton>

                <FloorToolButton
                  type="button"
                  active
                  onClick={saveReservationLayoutDraft}
                  disabled={isSavingLayout || !layoutContextReservation?.id}
                >
                  {isSavingLayout
                    ? t("ui.i18nCleanup2.aGuardar2")
                    : t("ui.i18nFinalCleanup.saveCount", { count: reservationLayoutDraftItems.length })}
                </FloorToolButton>
              </>
            )}
          </FloorHeaderActions>
        </FloorHeader>

        {arrangeMessage && (
          <ArrangeNote onClick={() => setArrangeMessage("")}>
            {arrangeMessage}
          </ArrangeNote>
        )}

        <FloorPlanArea>
          {zoneOptions.length > 0 ? (
            <>
              <ReservationFloorPlan
                allTables={allTables}
                zoneName={displayedFloor.activeZoneName}
                availabilityTables={displayedFloor.availabilityTables}
                selectedTables={displayedFloor.selectedTables}
                onSelectTable={handleFloorTableSelect}
                layoutOverrides={displayedFloor.layoutOverrides}
                layoutDraftItems={reservationLayoutDraftItems}
                /* Always draggable. A press that does not travel still selects
                   the booking, so no mode switch is needed - but a move can
                   only be saved against a booking, so that is the one gate. */
                enableMove={
                  !!layoutContextReservation?.id && !isSavingLayout
                }
                onLayoutItemChange={handleReservationLayoutItemChange}
                room={activeRoom}
                objects={roomObjects}
                upcomingTables={upcomingTables}
                upcomingLayoutOverrides={upcomingLayoutOverrides}
                /* Pinching invites a closer look than a 180% ceiling allows. */
                maxZoom={4}
              >
                {legend}
              </ReservationFloorPlan>
            </>
          ) : (
            <EmptyState>{t("ui.i18nCleanup.noRoomsOrTablesConfigured")}</EmptyState>
          )}
        </FloorPlanArea>
      </RightPanel>
    </BoardWrap>
  );
}
